import { SettlementDistribution } from '@/features/dashboard/bookingChanges/types/SettlementDistribution.ts';
import { SettlementOptionVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { moneyListDirection } from '@/features/dashboard/bookingChanges/utils/money.ts';

/**
 * Trzy karty rozkładu kwoty paczki, z czego działa jedna.
 *
 * Opis wariantu `SPREAD` mówił wcześniej o dzieleniu **równo** — backend dzieli
 * proporcjonalnie do wysokości rat, więc zdanie było nieprawdą o jedynym
 * wariancie, który naprawdę działa.
 *
 * Nazwa kwoty idzie za znakiem bilansu: paczka obniżająca rachunek jest zwrotem
 * i nazywanie jej „dopłatą" mówiło operatorowi coś odwrotnego do prawdy. Paczka
 * mieszana (dopłata w złotych, zwrot w euro) znaku nie ma, więc zostaje przy
 * neutralnej „kwocie paczki".
 */

interface KwotaNazwy {
  mianownik: string;
  dopelniacz: string;
  osobna: string;
  najblizsza: string;
}

const NAZWY: Record<'doplata' | 'zwrot' | 'neutralna', KwotaNazwy> = {
  doplata: {
    mianownik: 'Dopłata',
    dopelniacz: 'dopłaty',
    osobna: 'Osobna dopłata',
    najblizsza: 'Dopisz do najbliższej raty',
  },
  zwrot: {
    mianownik: 'Zwrot',
    dopelniacz: 'zwrotu',
    osobna: 'Osobny zwrot',
    najblizsza: 'Odejmij od najbliższej raty',
  },
  neutralna: {
    mianownik: 'Kwota paczki',
    dopelniacz: 'kwoty paczki',
    osobna: 'Osobna pozycja',
    najblizsza: 'Zapisz na najbliższej racie',
  },
};

const nazwyDla = (balance: MoneyValueDto[]): KwotaNazwy => {
  const direction = moneyListDirection(balance);
  if (direction === 1) {
    return NAZWY.doplata;
  }
  return direction === -1 ? NAZWY.zwrot : NAZWY.neutralna;
};

/** Karty rozkładu opisane kwotą tej paczki — dopłatą albo zwrotem. */
export const settlementDistributionOptions = (
  balance: MoneyValueDto[],
): SettlementOptionVm[] => {
  const nazwy = nazwyDla(balance);

  return [
    {
      option: SettlementDistribution.SPREAD,
      title: 'Rozłóż na pozostałe raty',
      description: `${nazwy.mianownik} wchodzi w raty jeszcze nieopłacone, proporcjonalnie do ich wysokości. Terminy zostają bez zmian.`,
      available: true,
    },
    {
      option: SettlementDistribution.NEAREST,
      title: nazwy.najblizsza,
      description: `Całość ${nazwy.dopelniacz} ląduje na pierwszej niezapłaconej racie.`,
      available: false,
    },
    {
      option: SettlementDistribution.SEPARATE,
      title: nazwy.osobna,
      description: `${nazwy.mianownik} jako nowa pozycja z własnym terminem; raty zostają nietknięte.`,
      available: false,
    },
  ];
};

/** Jedyny wariant, który backend potrafi wykonać — i domyślne zaznaczenie kroku W2. */
export const DEFAULT_SETTLEMENT_DISTRIBUTION = SettlementDistribution.SPREAD;

/** Dopisek przy wariantach, których backend jeszcze nie zna. */
export const SETTLEMENT_UNAVAILABLE_LABEL = 'dostępne wkrótce';
